import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, MapPin, Hash, Users, CheckCircle, XCircle, Phone } from 'lucide-react';
import { useAddressSearch } from '@/hooks/useAddressSearch';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';

const Auth = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  // Login form state - Email und OTP
  const [loginEmail, setLoginEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  
  // Registration form state
  const [signupData, setSignupData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    phone: ''
  });

  // Email validation states
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [emailCheckTimeout, setEmailCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  // Address search hook
  const {
    isLoadingAddress,
    addressSuggestions,
    selectedStreet,
    searchStep,
    searchStreets,
    searchHouseNumbers,
    selectAddressSuggestion,
    resetAddressSearch
  } = useAddressSearch();
  
  const addressInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Session check error:', error);
          // Clear potentially corrupted auth data
          localStorage.clear();
          return;
        }
        if (session) {
          navigate('/');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        localStorage.clear();
      }
    };
    checkUser();
  }, [navigate]);

  // Function to check if email already exists
  const checkEmailExists = async (email: string): Promise<boolean> => {
    // Check basic email format: etwas@etwas.etwas
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email.trim())) {
      setEmailExists(false);
      setIsCheckingEmail(false);
      return false;
    }

    setIsCheckingEmail(true);
    try {
      const { data: emailExists, error } = await supabase
        .rpc('check_user_email_exists', { user_email: email.trim() });

      if (error) {
        console.error('Email check error:', error);
        setEmailExists(false);
        return false;
      } else {
        const exists = emailExists || false;
        setEmailExists(exists);
        return exists;
      }
    } catch (error) {
      console.error('Email validation error:', error);
      setEmailExists(false);
      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Handle email change with debounce
  const handleEmailChange = (email: string) => {
    setSignupData({...signupData, email});
    
    // Clear existing timeout
    if (emailCheckTimeout) {
      clearTimeout(emailCheckTimeout);
    }
    
    // Set new timeout for email checking
    const timeout = setTimeout(() => {
      checkEmailExists(email);
    }, 500);
    
    setEmailCheckTimeout(timeout);
  };

  // Handle address change with search
  const handleAddressChange = (address: string) => {
    setSignupData({...signupData, address});

    if (searchStep === 'street') {
      searchStreets(address);
    } else if (searchStep === 'houseNumber' && selectedStreet) {
      // Extract house number from input
      const houseNumberPart = address.replace(selectedStreet.street, '').trim();
      if (houseNumberPart.length > 0) {
        searchHouseNumbers(
          selectedStreet.street,
          selectedStreet.city,
          selectedStreet.state,
          selectedStreet.postcode
        );
      }
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!loginEmail) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Prüfe ob Email existiert:', loginEmail);
      
      // Check if email exists before sending login link
      const emailExists = await checkEmailExists(loginEmail);
      
      console.log('Email existiert?', emailExists);
      
      if (!emailExists) {
        console.log('Email nicht gefunden');
        toast.error('Diese E-Mail ist nicht registriert. Bitte wenden Sie sich an Ihren Teamleiter.');
        setIsLoading(false);
        return;
      }

      console.log('Sende Login-Link an:', loginEmail);
      
      const origin = window.location.origin.replace(/:\d+$/, '');
      const { error } = await supabase.auth.signInWithOtp({
        email: loginEmail,
        options: {
          emailRedirectTo: `${origin}/email-confirmation`,
          shouldCreateUser: false, // Verhindert automatische User-Erstellung beim Login
        }
      });

      if (error) {
        console.error('Fehler beim Senden:', error);
        toast.error('Fehler beim Senden des Login-Links: ' + error.message);
        return;
      }

      console.log('Login-Link erfolgreich gesendet');
      setMagicLinkSent(true);
      setShowOtpInput(true);
      toast.success('Login-Link wurde an Ihre E-Mail gesendet! Sie können auch den 6-stelligen Code eingeben.');
    } catch (error: any) {
      console.error('Fehler in handleLogin:', error);
      toast.error('Ein Fehler ist aufgetreten: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🔐 Starting OTP verification for:', loginEmail);
    
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Bitte geben Sie den 6-stelligen Code ein');
      return;
    }

    setIsLoading(true);

    try {
      console.log('🔐 Calling verifyOtp with code:', otpCode);
      
      const { data, error } = await supabase.auth.verifyOtp({
        email: loginEmail,
        token: otpCode,
        type: 'email'
      });

      console.log('🔐 VerifyOtp response:', { hasData: !!data, hasSession: !!data?.session, error: error?.message });

      if (error) {
        console.error('🔐 OTP verification error:', error);
        toast.error('Fehler beim Verifizieren des Codes: ' + error.message);
        setIsLoading(false);
        return;
      }

      if (data?.session) {
        console.log('🔐 OTP verified successfully! User ID:', data.session.user.id);
        toast.success('Erfolgreich eingeloggt!');
        
        // Reset form state
        setMagicLinkSent(false);
        setShowOtpInput(false);
        setOtpCode('');
        setLoginEmail('');
        
        // SOFORTIGE Weiterleitung nach erfolgreichem Login
        navigate('/', { replace: true });
        setIsLoading(false);
      } else {
        console.error('🔐 No session in response');
        toast.error('Keine Session erhalten');
        setIsLoading(false);
      }
    } catch (error: any) {
      console.error('🔐 OTP verification exception:', error);
      toast.error('Ein Fehler ist aufgetreten: ' + error.message);
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    // SOFORTIGES preventDefault - verhindert jegliche weitere Form-Verarbeitung
    e.preventDefault();
    e.stopPropagation();
    
    console.log('handleSignup aufgerufen, isLoading:', isLoading);
    console.log('Form Daten:', signupData);
    
    // KRITISCH: Verhindere absolut jede weitere Ausführung wenn bereits loading
    if (isLoading) {
      console.log('Signup bereits in progress - abgebrochen');
      return;
    }
    
    // VOLLSTÄNDIGE VALIDIERUNG - KEIN SUPABASE AUFRUF BEI FEHLERN
    
    // Prüfe Vorname
    if (!signupData.firstName || !signupData.firstName.trim()) {
      console.log('Validierung fehlgeschlagen: Vorname fehlt');
      toast.error('Vorname ist erforderlich');
      return; // ABSOLUTER STOPP - kein Supabase Aufruf
    }
    
    // Prüfe Nachname  
    if (!signupData.lastName || !signupData.lastName.trim()) {
      console.log('Validierung fehlgeschlagen: Nachname fehlt');
      toast.error('Nachname ist erforderlich');
      return; // ABSOLUTER STOPP
    }
    
    // Prüfe E-Mail
    if (!signupData.email || !signupData.email.trim()) {
      console.log('Validierung fehlgeschlagen: E-Mail fehlt');
      toast.error('E-Mail ist erforderlich');
      return; // ABSOLUTER STOPP
    }
    
    // E-Mail Format prüfen - etwas@etwas.etwas
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(signupData.email.trim())) {
      console.log('Validierung fehlgeschlagen: E-Mail Format ungültig');
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return; // ABSOLUTER STOPP
    }
    
    // Prüfe ob Email bereits existiert
    if (emailExists) {
      console.log('Validierung fehlgeschlagen: E-Mail bereits registriert');
      toast.error('User already exists - Diese E-Mail-Adresse ist bereits registriert');
      return; // ABSOLUTER STOPP
    }
    
    // Prüfe Telefonnummer
    if (!signupData.phone || !signupData.phone.trim()) {
      console.log('Validierung fehlgeschlagen: Telefonnummer fehlt');
      toast.error('Telefonnummer ist erforderlich');
      return; // ABSOLUTER STOPP
    }
    
    // ALLE VALIDIERUNGEN ERFOLGREICH - jetzt darf Supabase aufgerufen werden
    console.log('Alle Validierungen erfolgreich - starte Supabase Registrierung');
    setIsLoading(true);

    try {
      // Port aus der URL entfernen für Produktionsumgebung
      const origin = window.location.origin.replace(/:\d+$/, '');
      const redirectUrl = `${origin}/email-confirmation`;
      
      console.log('Rufe supabase.auth.signUp auf mit:', {
        email: signupData.email
      });
      
      // WICHTIG: signUp verwenden für Registrierung, nicht signInWithOtp!
      // signUp erstellt einen neuen User in auth.users und triggert handle_new_user
      const { data, error } = await supabase.auth.signUp({
        email: signupData.email,
        password: crypto.randomUUID(), // Temporäres Passwort (wird nicht verwendet, da Magic Link)
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: signupData.firstName,
            last_name: signupData.lastName,
            address: signupData.address,
            phone: signupData.phone
          }
        }
      });

      console.log('Supabase Antwort:', { data: data ? 'DATA_VORHANDEN' : 'KEINE_DATA', error });

      if (error) {
        if (error.message.includes('User already registered')) {
          // User existiert bereits - zur Login-Tab wechseln
          setLoginEmail(signupData.email);
          setActiveTab('login');
          toast.error('Benutzer bereits registriert. Bitte melden Sie sich an.');
        } else {
          toast.error(error.message);
        }
        return;
      }

      toast.success('Registrierung erfolgreich! Bitte prüfen Sie Ihre E-Mails für den Bestätigungslink.');
      setSignupSuccess(true);
    } catch (error: any) {
      console.error('Fehler beim Signup:', error);
      toast.error('Ein Fehler ist aufgetreten: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };


  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    
    try {
      const origin = window.location.origin.replace(/:\d+$/, '');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${origin}/email-confirmation`,
        }
      });

      if (error) {
        toast.error('Google-Anmeldung fehlgeschlagen: ' + error.message);
      }
    } catch (error: any) {
      toast.error('Ein Fehler ist aufgetreten: ' + error.message);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Willkommen
          </h1>
          <p className="text-muted-foreground">
            Melden Sie sich an
          </p>
        </div>

        <Card className="shadow-lg border-0 bg-card/50 backdrop-blur-sm">
          <CardContent className="pt-6">
                <form onSubmit={handleLogin} className="space-y-4">
                  {!magicLinkSent ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="login-email">E-Mail</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="login-email"
                            type="email"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            required
                            className="pl-10"
                          />
                        </div>
                      </div>
                      
                      <div className="text-sm text-muted-foreground text-center">
                        Sie erhalten einen Login-Link per E-Mail
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
                        size="lg"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        Login-Link senden
                      </Button>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center p-6 bg-success/10 border border-success/20 rounded-lg">
                        <div className="text-center space-y-3">
                          <CheckCircle className="h-12 w-12 text-success mx-auto" />
                          <p className="text-lg font-medium text-success">
                            Login-Link gesendet!
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Wir haben Ihnen einen Login-Link an<br />
                            <strong>{loginEmail}</strong> gesendet.
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Klicken Sie auf den Link in der E-Mail, um sich anzumelden.
                          </p>
                        </div>
                      </div>
                      
                      {showOtpInput && (
                        <div className="space-y-4">
                          <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                              <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-card px-2 text-muted-foreground">ODER TOKEN EINGEBEN</span>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="relative">
                              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input
                                id="otp-code"
                                type="text"
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && otpCode.length === 6) {
                                    e.preventDefault();
                                    handleVerifyOtp(e as any);
                                  }
                                }}
                                placeholder="******"
                                maxLength={6}
                                className="pl-10 text-center text-lg tracking-widest"
                              />
                            </div>
                          </div>
                          
                          <Button 
                            type="button"
                            onClick={handleVerifyOtp}
                            className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
                            size="lg"
                            disabled={isLoading || otpCode.length !== 6}
                          >
                            {isLoading ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            Mit Token anmelden
                          </Button>
                        </div>
                      )}
                      
                      <Button
                        type="button"
                        onClick={() => {
                          setMagicLinkSent(false);
                          setShowOtpInput(false);
                          setOtpCode('');
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        Andere E-Mail verwenden
                      </Button>
                    </div>
                  )}
                </form>
          </CardContent>
        </Card>
        
        <p className="text-center text-sm text-muted-foreground">
          Durch die Anmeldung stimmen Sie unseren Nutzungsbedingungen zu.
        </p>
      </div>
    </div>
  );
};

export default Auth;